/**
 * Type part of a customer's name, pick one from the list. A small combobox
 * over GET /customers (name, code, TIN, city and contact names are searched).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api } from '../lib/api';
import { inputClass } from './ui';

export interface PickedCustomer {
  id: string;
  name: string;
  code?: string;
}

interface CustomerOption {
  id: string;
  code: string;
  name: string;
  city: string | null;
}

export function CustomerSearch({
  value,
  onChange,
  placeholder = 'Customer name',
  className,
}: {
  value: PickedCustomer | null;
  onChange: (customer: PickedCustomer | null) => void;
  placeholder?: string;
  className?: string;
}): JSX.Element {
  const listId = useId();
  const [text, setText] = useState(value?.name ?? '');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  // Keep the box in step with the picked customer (e.g. cleared from outside).
  useEffect(() => setText(value?.name ?? ''), [value?.id, value?.name]);

  // Wait for a pause in typing before asking the API.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), 200);
    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    const close = (event: MouseEvent): void => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const searching = open && term.length >= 1 && term !== value?.name;
  const options = useQuery({
    queryKey: ['customers', 'typeahead', term],
    queryFn: () =>
      api<{ data: CustomerOption[] }>(
        `/customers?pageSize=8&q=${encodeURIComponent(term)}`,
      ),
    enabled: searching,
    staleTime: 30_000,
  });
  const list = searching ? (options.data?.data ?? []) : [];

  const pick = (option: CustomerOption): void => {
    onChange({ id: option.id, name: option.name, code: option.code });
    setText(option.name);
    setOpen(false);
  };

  return (
    <div ref={box} className={`relative ${className ?? ''}`}>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
      />
      <input
        role="combobox"
        aria-expanded={list.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Customer"
        aria-activedescendant={list[active] ? `${listId}-${list[active].id}` : undefined}
        className={`${inputClass} pl-9 ${value ? 'pr-9' : ''}`}
        placeholder={placeholder}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          setOpen(true);
          if (value && event.target.value !== value.name) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActive((index) => Math.min(index + 1, Math.max(list.length - 1, 0)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && list[active]) {
            event.preventDefault();
            pick(list[active]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {value && (
        <button
          type="button"
          aria-label="Clear customer"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          onClick={() => {
            onChange(null);
            setText('');
          }}
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
      {searching && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {options.isLoading && <li className="px-3 py-2 text-sm text-slate-500">Searching…</li>}
          {!options.isLoading && list.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">No customer matches “{term}”.</li>
          )}
          {list.map((option, index) => (
            <li
              key={option.id}
              id={`${listId}-${option.id}`}
              role="option"
              aria-selected={index === active}
              className={`cursor-pointer px-3 py-2 text-sm ${
                index === active
                  ? 'bg-brand-50 text-navy dark:bg-slate-800 dark:text-white'
                  : 'text-slate-700 dark:text-slate-200'
              }`}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(option);
              }}
            >
              <span className="font-medium">{option.name}</span>
              <span className="ml-2 font-mono text-xs text-slate-500">{option.code}</span>
              {option.city && <span className="ml-2 text-xs text-slate-500">· {option.city}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
